<script setup>
import { defineProps, ref, watch, computed, onMounted } from "vue";
import { useDisplay } from "vuetify";

const { name, width } = useDisplay();

import { Line } from "vue-chartjs";
// import { computed } from "vue";

import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
} from "chart.js";

// Register Chart.js components
ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, LinearScale, CategoryScale);
const moodLegend = ref([
  { range: "1-3", description: "Calm 😊", color: "blue" },
  { range: "4-5", description: "Neutral 😐", color: "purple" },
  { range: "6-7", description: "Stressed 😟", color: "red" }
]);
const anxietyLegend = ref([
  { range: "1-3", description: "Happy 😊", color: "green" },
  { range: "4-5", description: "Unhappy 😟", color: "orange" },
  { range: "6-7", description: "Danger ⚠️", color: "red" }
]);



// Define props
const props = defineProps({
  chartData: Object,
  chartType: String, // Accept "Mood" or "Anxiety"
  enabledType: Boolean
});
const selectedLegend = computed(() => {
  return props.chartType === "Mood" ? moodLegend.value : anxietyLegend.value;
});
const chartOptions = ref({
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: { left: 20, right: 20, top: 10, bottom: 10 }
  },
  plugins: {
    title: {
      display: false,
      // text: `${props.chartType} Levels Over Time`
    },
    legend: {
      display: false,
    },
  },
  scales: {
    x: {
      title: { display: true, text: "Date" },
      ticks: {
        callback: function (val) {
          return this.getLabelForValue(val).split("T")[0];
        },
        autoSkip: true,
        autoSkipPadding: 15,
      }

    },
    y: {
      title: { display: true, text: "Anxiety Level" },
      min: 0,
      max: 7,
    },
  },
});

onMounted(() => {
  if (props.chartType) {
    updateOptions(props.chartType);
  }
});

const updateOptions = (newType) => {
  // To handle deep mutation for an object, manually update field
  chartOptions.value = {
    ...chartOptions.value, // Spread existing options
    plugins: {
      ...chartOptions.value.plugins,
      title: {
        display: true,
        text: `${props.chartType} Levels Over Time`
      },
    },
    scales: {
      ...chartOptions.value.scales,
      y: {
        ...chartOptions.value.scales.y,
        title: {
          ...chartOptions.value.scales.y.title,
          text: newType + " Level",
        },
      },
    },
  };
}

// ✅ Watch for `chartType` changes and update Y-axis title
watch(() => props.chartType, (newType) => {
  updateOptions(newType);
});

// Ensure chart updates when chartData changes
const reactiveChartData = ref(props.chartData);
watch(() => props.chartData, (newData) => {
  if (newData) {
    reactiveChartData.value = newData;
  }
});

const chartDimensions = computed(() => {
  // name is reactive and must use .value
  switch (name.value) {
      case 'xs': return {width: "200px", height: "200px"}
      case 'sm': return {width: "90%", height: "300px"}
      case 'md': return {width: "90%", height: "400px"}
      case 'lg': return {width: "90%", height: "600px"}
      case 'xl': return {width: "90%", height: "800px"}
      case 'xxl': return {width: "90%", height: "800px"}
  }
  return {};
});

const chartKey = ref(0); // Key to force chart re-render

// Watch for screen width changes
watch(width, () => {
  chartKey.value += 1; // Change the key to trigger a re-render
});

</script>

<template>

  <!-- Custom Legend Above Chart -->

   <div v-if="enabledType" class="legend-container">
    <h3 class="legend-title">{{ chartType }} Score Meaning</h3>
    <div class="
    legend-grid">
      <div v-for="item in selectedLegend" :key="item.range" class="legend-item">
        <span class="legend-color-box" :style="{ backgroundColor: item.color }"></span>
        <span class="legend-score">{{ item.range }}:</span>
        <span class="legend-description">{{ item.description }}</span>
      </div>
    </div>
  </div>

  <div class="d-flex justify-center chart-container">
    <!-- Chart (No Custom Coloring) -->
    <Line :key="chartKey" v-if="chartData && chartData.datasets.length" 
    :data="chartData" 
    :options="chartOptions"
    :style="chartDimensions" />
  </div>
  
</template>

<style scoped>
/* Legend Styling */
.legend-container {
  margin-top: 20px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
  text-align: center;
}

.legend-title {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.legend-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
  padding: 10px;
}

.legend-item {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  padding: 4px;
}

.legend-score {
  font-weight: bold;
  margin-right: 5px;
}

.legend-description {
  color: #555;
}

.legend-color-box {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
}

.custom-card {
  width: 100%;
  height: 100%;
}

.chart-container {
  position: relative;
  height: 40vh;
  width: 80vw;
  max-width: 100%; /* Prevents overflow */
}

.chartMenu {
  width: 100vw;
  height: 40px;
  background: #1A1A1A;
  color: rgba(54, 162, 235, 1);
}

.chartMenu p {
  padding: 10px;
  font-size: 20px;
}

.chartCard {
  width: 100vw;
  height: calc(100vh - 40px);
  background: rgba(54, 162, 235, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-box {
  display: flex;
  width: 90%;
  height: 45%; 
  justify-content: center;
  background: white;
}

/* Bascially an If statement: if * and * */
@media only screen and (max-width: 600px) {
  .chart-box {
    width: 45%;
    height: 45%;
  }
}
</style>